import { Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { RaptorJobFormValues } from '../../../../RaptorJobValidationSchema';
import tid from '@/common/application/utils/tid';
import { FormikDateTimePicker } from '@/common/presentation/components/forms/fields/FormikDateTimePicker';
import { useERPCustomerInfoSection } from '../../hook/useERPCustomerInfoSection';
import { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';

import type { CallerDto } from '@/libs/api-rest/generated/model';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';

interface CustomerInfoSectionProps {
  onAddNewCustomer: () => void;
  onCustomerChanged: () => void;
  setSelectedCustomerName?: (term: string) => void;
  mode?: JobFormMode;
  rawCaller?: string | null;
  callerInfo?: CallerDto | null;
  callerRequired?: boolean;
}

export function ERPCustomerInfoSection({
  onAddNewCustomer,
  onCustomerChanged,
  setSelectedCustomerName,
  mode = JOB_FORM_MODES.CREATE,
  rawCaller,
  callerInfo,
  callerRequired = false
}: CustomerInfoSectionProps) {
  const {
    setCustomerSearchTerm,
    customerOptions,
    customerLoading,
    customerFilterOptions,
    customerCallerLoading,
    getContactAutocompleteOptions,
    getContactInitialOption,
    customerInitialOption,
    values,
    initialCallerId
  } = useERPCustomerInfoSection(rawCaller, callerInfo);

  const { setFieldValue } = useFormikContext<RaptorJobFormValues>();

  const contactOptions = getContactAutocompleteOptions();
  const contactInitialOption = getContactInitialOption();

  // Track when contacts finish loading to force re-render of autocomplete
  const contactsLoadedKey = useRef(0);
  useEffect(() => {
    if (!customerCallerLoading && contactOptions.length > 0 && initialCallerId != null) {
      contactsLoadedKey.current += 1;
    }
  }, [customerCallerLoading, contactOptions.length, initialCallerId]);

  // Set callerId when initialCallerId becomes available (after contacts load)
  // This ensures the form field value is set so Formik tracks it properly
  useEffect(() => {
    // Only set if we have a valid ID (not 0 or null) and callerId is not already set to a different valid value
    if (
      initialCallerId != null &&
      initialCallerId !== 0 &&
      initialCallerId !== '0' &&
      (values.callerId == null || values.callerId === 0) &&
      !customerCallerLoading &&
      contactOptions.length > 0 // Ensure options are loaded
    ) {
      setFieldValue('callerId', initialCallerId, false); // false = don't validate on set
    }
  }, [initialCallerId, values.callerId, customerCallerLoading, setFieldValue, contactOptions.length]);

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: '12px', fontWeight: 600 }}>
        Customer Information
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr' },
          gap: '12px',
          width: '100%'
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          id="customerId"
          label="Customer"
          options={customerOptions}
          optionsloading={customerLoading}
          filterOptions={customerFilterOptions}
          onInputChange={(_, inputValue) => setCustomerSearchTerm(inputValue)}
          getOptionLabel={(option: OptionType) => option?.label ?? 'Unknown Customer'}
          isOptionEqualToValue={(option: OptionType, value: OptionType) => option?.value === value?.value}
          initialOption={customerInitialOption}
          actions={
            mode === JOB_FORM_MODES.EDIT
              ? []
              : [
                  {
                    label: 'Add New Customer',
                    icon: <Add />,
                    onClick: onAddNewCustomer
                  }
                ]
          }
          required
          skipValidationOnActionClick
          disabled={mode === JOB_FORM_MODES.EDIT}
          onChange={(value) => {
            if (value && 'label' in value) {
              const customerName = value.label;
              setFieldValue('customerName', customerName);
              setSelectedCustomerName?.(customerName);
            } else {
              setFieldValue('customerName', null);
              setSelectedCustomerName?.('');
            }
            onCustomerChanged();
          }}
          sx={{ width: '100%' }}
          {...tid('jobs:create:customer-autocomplete')}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '12px',
          width: '100%',
          mt: 1
        }}
      >
        <FormikAutocomplete<OptionType, RaptorJobFormValues>
          key={`caller-id-${values.customerId}-${initialCallerId}-${contactsLoadedKey.current}`}
          id="callerId"
          label="Caller ID"
          options={contactOptions}
          optionsloading={customerCallerLoading}
          filterOptions={(options, state) => {
            const input = state.inputValue.trim().toLowerCase();
            if (!input) return options;
            return options.filter((o) => o.label.toLowerCase().includes(input));
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
          placeholder={customerCallerLoading ? 'Loading contacts...' : 'Select a contact'}
          disabled={!values.customerId || customerCallerLoading || contactOptions.length === 0}
          initialOption={contactInitialOption}
          required={callerRequired}
          sx={{ width: '100%', minWidth: 0 }}
          {...tid('jobs:create:caller-autocomplete')}
        />
        <FormikDateTimePicker<RaptorJobFormValues> id="calledAt" label="Called In Time" required testId="jobs:create:called-at-input" />
      </Box>
    </>
  );
}
